import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { useNavigate } from 'react-router-dom';
import { Item } from '../../types/Item';
import { Employee } from '../layout/Navbar';
import { DraggableItem } from './ItemComponents';
import { CanvasItem } from './ItemComponents';
import { PriceSummary } from './PriceSummary';

/* ==================== LEFT PANEL ==================== */
interface LeftPanelProps {
  items: Item[];
  onAddItem: (name: string, price: number) => void;
  onMoveItem: (item: Item) => void;
}

export const LeftPanel: React.FC<LeftPanelProps> = ({ items, onAddItem, onMoveItem }) => {
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleAddItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (newItemName.trim() && newItemPrice.trim()) {
      const price = parseFloat(newItemPrice);
      if (!isNaN(price) && price > 0) {
        onAddItem(newItemName.trim(), price);
        setNewItemName('');
        setNewItemPrice('');
        setIsExpanded(false);
      }
    }
  };

  return (
    <div className="left-panel">
      <div className="panel-header">
        <h2>Beschikbare Items</h2>
        <p>Voeg items toe of sleep ze naar het canvas</p>
      </div>

      <div className="items-list">
        <div className="items-header">
          <h3>Items ({items.length})</h3>
          <div className="items-header-actions">
            <button 
              className="add-item-button"
              onClick={() => setIsExpanded(!isExpanded)}
              title="Item toevoegen"
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                className={`plus-icon ${isExpanded ? 'rotated' : ''}`}
              >
                <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button 
              className="mediakit-icon-button"
              onClick={() => window.open('/MediaKit.pdf', '_blank', 'noopener,noreferrer')}
              title="Open Media Kit"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 4.5C7 4.22386 7.22386 4 7.5 4H14L18 8V19.5C18 19.7761 17.7761 20 17.5 20H7.5C7.22386 20 7 19.7761 7 19.5V4.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 4V8H18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 13L15 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 16H12.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
        
        {isExpanded && (
          <div className="add-item-form">
            <h4>Nieuw Item Toevoegen</h4>
            <form onSubmit={handleAddItem}>
              <div className="form-group">
                <label htmlFor="itemName">Item Naam:</label>
                <input
                  type="text"
                  id="itemName"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Voer item naam in"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="itemPrice">Prijs (€):</label>
                <input
                  type="number"
                  id="itemPrice"
                  value={newItemPrice}
                  onChange={(e) => setNewItemPrice(e.target.value)}
                  placeholder="Voer prijs in"
                  step="0.01"
                  min="0"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={() => setIsExpanded(false)}>
                  Annuleren
                </button>
                <button type="submit" className="add-button">
                  Toevoegen
                </button>
              </div>
            </form>
          </div>
        )}
        
        <div className="items-container">
          {items.length === 0 ? (
            <div className="empty-state">
              <p>Nog geen items toegevoegd</p>
              <p>Klik op de + knop om een item toe te voegen</p>
            </div>
          ) : (
            items.map((item) => (
              <DraggableItem
                key={item.id}
                item={item}
                onMove={onMoveItem}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

/* ==================== RIGHT PANEL ==================== */
interface RightPanelProps {
  items: Item[];
  onRemoveItem: (itemId: string) => void;
  notes?: string;
  selectedEmployee: Employee;
  discount?: number;
  onDiscountChange?: (discount: number) => void;
  showDiscount?: boolean;
}

export const RightPanel: React.FC<RightPanelProps> = ({ 
  items, 
  onRemoveItem, 
  notes = '', 
  selectedEmployee, 
  discount = 0, 
  onDiscountChange,
  showDiscount = false
}) => {
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
  const navigate = useNavigate();
  
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'item',
    drop: () => {},
    collect: (monitor: any) => ({
      isOver: monitor.isOver(),
    }),
  }));

  // Calculate subtotal excluding items with price 0 (op maat items)
  const subtotal = items.reduce((sum, item) => sum + (item.price || 0), 0);
  const discountAmount = subtotal * (discount / 100);
  const totalPrice = subtotal - discountAmount;

  const handleOrder = () => {
    navigate('/pakketoverzicht', {
      state: {
        items,
        totalPrice,
        notes,
        employee: selectedEmployee
      }
    });
  };

  return (
    <div className={`right-panel ${isSummaryExpanded ? 'summary-expanded' : ''}`}>
      <div className="panel-header">
        <h2>Klant Pakket Canvas</h2>
        <p>Sleep items hierheen om het pakket samen te stellen</p>
      </div>

      <div 
        ref={drop}
        className={`canvas-area ${isOver ? 'drag-over' : ''}`}
      >
        <div className="canvas-content">
          {items.length === 0 ? (
            <div className="empty-canvas">
              <div className="empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Leeg Canvas</h3>
              <p>Sleep items van links naar hier om het pakket samen te stellen</p>
            </div>
          ) : (
            <div className="canvas-items">
              {items.map((item) => (
                <CanvasItem
                  key={item.id}
                  item={item}
                  onRemove={() => onRemoveItem(item.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="summary-section">
        {isSummaryExpanded && (
          <div className="summary-content">
            <PriceSummary 
              items={items}
              subtotal={subtotal}
              discount={discount}
              discountAmount={discountAmount}
              totalPrice={totalPrice}
              notes={notes}
              onDiscountChange={onDiscountChange}
              showDiscount={showDiscount}
            />
          </div>
        )}
        
        <div className="total-display">
          <div className="summary-total">Totaal: €{totalPrice.toFixed(2)}</div>
          <svg 
            className={`dropdown-arrow ${isSummaryExpanded ? 'expanded' : ''}`}
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
          >
            <path d="M18 15L12 9L6 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <button className="summary-button" onClick={handleOrder}>
          Totale overzicht
        </button>
      </div>
    </div>
  );
};
